/**
 * SvgIcons.js — Nomad brand illustration & icon library
 *
 * Colors come from the design tokens in index.css, not from literals — pass a
 * CSS variable as `color` (e.g. color="var(--text-on-band)") and the icon
 * follows the active theme. The five brand colors are Ink / Royal / Teal /
 * Mint / Paper; see the token table at the top of index.css.
 */

import nomadShield from '../assets/nomad-shield.png';

// ─── Brand / Logo ─────────────────────────────────────────────────────────────

/**
 * NomadHomeIcon — the house-in-shield matching the Nomad app icon.
 * A location-pin shield with a house (roof + eaves + 2×2 window grid) inside.
 */
export function NomadHomeIcon({ size = 24, color = 'var(--text-primary)', fill = 'var(--chip-royal-bg)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Shield / location-pin shell */}
      <path
        d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2Z"
        fill={fill} stroke={color} strokeWidth="1.6"
      />
      {/* Roof peak → eave line */}
      <polyline
        points="7,9.6 12,5.8 17,9.6"
        stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"
      />
      {/* Extended eave bar */}
      <line x1="6.2" y1="9.6" x2="17.8" y2="9.6" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
      {/* Left wall */}
      <line x1="8" y1="9.6" x2="8" y2="15.4" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
      {/* Right wall */}
      <line x1="16" y1="9.6" x2="16" y2="15.4" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
      {/* 2×2 window panes (solid squares) */}
      <rect x="9.3" y="10.6" width="2.4" height="2.4" fill={color} rx="0.25" />
      <rect x="12.3" y="10.6" width="2.4" height="2.4" fill={color} rx="0.25" />
      <rect x="9.3" y="13.2" width="2.4" height="2.4" fill={color} rx="0.25" />
      <rect x="12.3" y="13.2" width="2.4" height="2.4" fill={color} rx="0.25" />
    </svg>
  );
}

/**
 * NomadPinLogo — the real Nomad pin/shield brand mark (raster), for spots that
 * should show the actual logo rather than the line-art HouseIllustration /
 * NomadHomeIcon glyphs — e.g. the auth-screen badge circles and the role picker.
 * Square box, aspect ratio preserved.
 */
export function NomadPinLogo({ size = 40, style }) {
  return (
    <img
      src={nomadShield}
      alt="Nomad"
      style={{ width: `${size}px`, height: `${size}px`, objectFit: 'contain', display: 'block', ...style }}
    />
  );
}

/**
 * BrandLogo — the Nomad lockup: pin/house mark + "nomad" wordmark.
 * Single source of truth for the logo across the top bar, sidebar, and profile
 * headers. To swap in a raster logo later, replace the contents of this one
 * component (e.g. an <img src=.../>) and every placement updates at once.
 */
export function BrandLogo({ size = 21, color = 'var(--text-primary)', markSize }) {
  const mark = markSize != null ? markSize : size + 5;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '9px' }}>
      <img
        src={nomadShield}
        alt="Nomad"
        style={{ height: `${mark}px`, width: 'auto', display: 'block' }}
      />
      <span
        style={{
          fontSize: `${size}px`,
          color,
          fontWeight: 500,
          letterSpacing: '.02em',
          fontFamily: "'Quicksand', 'Quicksand Book', 'Helvetica Neue', sans-serif",
          lineHeight: 1,
        }}
      >
        nomad
      </span>
    </span>
  );
}

// ─── Official Social Platform Logos ──────────────────────────────────────────

export function InstagramIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="ig-rg" cx="30%" cy="110%" r="130%">
          <stop offset="0%"  stopColor="#FFD676" />
          <stop offset="28%" stopColor="#F86F2D" />
          <stop offset="54%" stopColor="#DE2E74" />
          <stop offset="80%" stopColor="#A82DBF" />
        </radialGradient>
      </defs>
      <rect width="24" height="24" rx="6" fill="url(#ig-rg)" />
      <rect x="6.5" y="6.5" width="11" height="11" rx="3" fill="none" stroke="white" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="3" fill="none" stroke="white" strokeWidth="1.5" />
      <circle cx="16.5" cy="7.5" r="0.85" fill="white" />
    </svg>
  );
}

export function FacebookIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="24" rx="6" fill="#1877F2" />
      <path
        d="M15.2 12.5l.35-2.28h-2.19V8.74c0-.62.31-1.23 1.29-1.23h1v-1.94s-.9-.15-1.77-.15c-1.8 0-2.98 1.09-2.98 3.07v1.74H8.9v2.28h1.99v5.51h2.46v-5.51h1.84z"
        fill="white"
      />
    </svg>
  );
}

export function LinkedInIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="24" rx="4" fill="#0A66C2" />
      <rect x="5" y="9.5" width="3" height="9" fill="white" />
      <circle cx="6.5" cy="6.5" r="1.75" fill="white" />
      <path d="M10.5 9.5h2.8v1.25c.42-.72 1.32-1.45 2.65-1.45C18.3 9.3 19 11.1 19 13.4V18.5h-3v-4.55c0-1.08-.02-2.47-1.5-2.47-1.52 0-1.8 1.19-1.8 2.42V18.5h-2.8V9.5z" fill="white" />
    </svg>
  );
}

export function LinkIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="var(--text-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="var(--text-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Empty-State Illustrations ───────────────────────────────────────────────

export function HouseIllustration({ size = 64 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Shield/pin shape */}
      <path d="M32 4C22 4 13 12 13 22C13 36 32 58 32 58C32 58 51 36 51 22C51 12 42 4 32 4Z" fill="var(--chip-royal-bg)" stroke="var(--text-primary)" strokeWidth="3" />
      {/* Roof */}
      <polyline points="17,24 32,13 47,24" stroke="var(--text-primary)" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      {/* Eave bar */}
      <line x1="14.5" y1="24" x2="49.5" y2="24" stroke="var(--text-primary)" strokeWidth="3.5" strokeLinecap="round" />
      {/* Walls */}
      <line x1="19.5" y1="24" x2="19.5" y2="38" stroke="var(--text-primary)" strokeWidth="3" strokeLinecap="round" />
      <line x1="44.5" y1="24" x2="44.5" y2="38" stroke="var(--text-primary)" strokeWidth="3" strokeLinecap="round" />
      {/* 2×2 window panes */}
      <rect x="24" y="26" width="7" height="7" fill="var(--text-primary)" rx="0.8" />
      <rect x="33" y="26" width="7" height="7" fill="var(--text-primary)" rx="0.8" />
      <rect x="24" y="34.5" width="7" height="7" fill="var(--text-primary)" rx="0.8" />
      <rect x="33" y="34.5" width="7" height="7" fill="var(--text-primary)" rx="0.8" />
    </svg>
  );
}

export function SearchIllustration({ size = 64 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="26" cy="26" r="18" fill="var(--chip-royal-bg)" />
      <circle cx="26" cy="26" r="14" fill="none" stroke="var(--text-primary)" strokeWidth="4" />
      <line x1="36" y1="36" x2="56" y2="56" stroke="var(--text-primary)" strokeWidth="4.5" strokeLinecap="round" />
      <path d="M19 26a7 7 0 0 1 7-7" stroke="var(--text-link)" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

export function ChatIllustration({ size = 64 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="4" y="6" width="44" height="32" rx="10" fill="var(--text-primary)" />
      <polygon points="10,38 4,54 22,46" fill="var(--text-primary)" />
      <rect x="12" y="17" width="28" height="3" rx="1.5" fill="white" fillOpacity="0.7" />
      <rect x="12" y="25" width="18" height="3" rx="1.5" fill="white" fillOpacity="0.45" />
      <rect x="24" y="30" width="34" height="24" rx="8" fill="var(--text-link)" />
      <polygon points="52,54 58,62 46,58" fill="var(--text-link)" />
      <rect x="30" y="38" width="22" height="2.5" rx="1.25" fill="white" fillOpacity="0.7" />
      <rect x="30" y="45" width="14" height="2.5" rx="1.25" fill="white" fillOpacity="0.45" />
    </svg>
  );
}

export function LikeIllustration({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="9" width="42" height="30" rx="5" fill="var(--text-primary)" />
      <path d="M3 15l21 13 21-13" stroke="white" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M24 32c-1.5-2-7-5-7 0 0 3 7 8 7 8s7-5 7-8c0-5-5.5-2-7 0z" fill="white" />
    </svg>
  );
}

export function MatchIllustration({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="20" fill="var(--text-link)" />
      <path d="M13 24l8 8 14-16" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── UI Icons ─────────────────────────────────────────────────────────────────

export function BellIcon({ size = 18, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 3a2 2 0 0 0-2 2c0 .18.02.36.06.52C7.88 6.37 6 8.52 6 11v5l-2 2v1h16v-1l-2-2v-5c0-2.48-1.88-4.63-4.06-5.48.04-.16.06-.34.06-.52a2 2 0 0 0-2-2zm0 18a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2z"
        fill={color}
      />
    </svg>
  );
}

export function LightningIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill={color} />
    </svg>
  );
}

export function MessageIcon({ size = 20, color = 'var(--text-primary)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="2" width="15" height="12" rx="4" fill={color} />
      <polygon points="3,14 1,19 8,16" fill={color} />
    </svg>
  );
}

export function HeartEnvelopeIcon({ size = 20, color = 'var(--text-primary)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="4" width="18" height="13" rx="2.5" fill={color} />
      <path d="M1 7.5l9 5.5 9-5.5" stroke="white" strokeWidth="1.2" />
      <path d="M10 14.5c-.7-1-3-2.5-3 .5 0 2 3 3.5 3 3.5s3-1.5 3-3.5c0-3-2.3-1.5-3-.5z" fill="white" />
    </svg>
  );
}

export function MatchBadgeIcon({ size = 20, color = 'var(--text-accent)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="10" r="9" fill={color} />
      <path d="M5 10l4 4 6-8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Navigation / Action Icons ────────────────────────────────────────────────

export function ArrowLeftIcon({ size = 18, color = 'var(--text-primary)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 12H5M5 12l7-7M5 12l7 7" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function CloseXIcon({ size = 16, color = 'var(--text-muted)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 6L6 18M6 6l12 12" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

export function MenuIcon({ size = 20, color = 'var(--text-primary)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="4" y1="7" x2="20" y2="7" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <line x1="4" y1="12" x2="20" y2="12" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <line x1="4" y1="17" x2="20" y2="17" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function AlertIcon({ size = 19, color = 'var(--text-primary)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="2" fill="none" />
      <line x1="12" y1="7" x2="12" y2="13" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="16.5" r="1.15" fill={color} />
    </svg>
  );
}

export function HomeIcon({ size = 18, color = 'var(--text-primary)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 11.5L12 4l9 7.5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.5 10v9.5h13V10" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 19.5v-5h4v5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function FilterIcon({ size = 16, color = 'var(--text-primary)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="4" y1="6" x2="20" y2="6" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <line x1="7" y1="12" x2="17" y2="12" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <line x1="10" y1="18" x2="14" y2="18" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function EditPencilIcon({ size = 16, color = 'var(--text-primary)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function PauseIcon({ size = 16, color = 'var(--text-primary)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="6" y="4" width="4" height="16" rx="1.5" fill={color} />
      <rect x="14" y="4" width="4" height="16" rx="1.5" fill={color} />
    </svg>
  );
}

export function PlayIcon({ size = 16, color = 'var(--text-primary)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5 3l14 9-14 9V3z" fill={color} />
    </svg>
  );
}

export function TrashIcon({ size = 16, color = 'var(--danger)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="10" y1="11" x2="10" y2="17" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <line x1="14" y1="11" x2="14" y2="17" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function LockIcon({ size = 16, color = 'var(--danger)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="5" y="11" width="14" height="10" rx="2.5" stroke={color} strokeWidth="2" fill="none" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="16" r="1.5" fill={color} />
    </svg>
  );
}

export function LocationPinIcon({ size = 14, color = 'var(--text-accent)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill={color} />
      <circle cx="12" cy="9" r="2.5" fill="white" />
    </svg>
  );
}

export function PriceTagIcon({ size = 14, color = 'var(--text-accent)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2L3 11l10 10 9-9V3h-9L12 2z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <circle cx="16" cy="7" r="1.5" fill={color} />
      <line x1="9" y1="15" x2="15" y2="9" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export function BedIcon({ size = 14, color = 'var(--text-accent)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 9v6h18V9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M3 15v2M21 15v2" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <path d="M3 9V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" />
      <rect x="5" y="8" width="5" height="3" rx="1" fill={color} />
      <rect x="14" y="8" width="5" height="3" rx="1" fill={color} />
    </svg>
  );
}

export function BathIcon({ size = 14, color = 'var(--text-accent)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5 17a4 4 0 0 0 4 4h6a4 4 0 0 0 4-4v-2H5v2z" fill={color} />
      <rect x="3" y="11" width="18" height="4" rx="2" stroke={color} strokeWidth="2" fill="none" />
      <path d="M7 11V6a2 2 0 0 1 4 0v1" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function CalendarIcon({ size = 14, color = 'var(--text-accent)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="4" width="18" height="18" rx="3" stroke={color} strokeWidth="2" fill="none" />
      <line x1="3" y1="10" x2="21" y2="10" stroke={color} strokeWidth="2" />
      <line x1="8" y1="2" x2="8" y2="6" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <line x1="16" y1="2" x2="16" y2="6" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <rect x="7" y="14" width="3" height="3" rx="0.5" fill={color} />
      <rect x="14" y="14" width="3" height="3" rx="0.5" fill={color} />
    </svg>
  );
}

export function CheckCircleIcon({ size = 14, color = 'var(--success)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" fill="none" />
      <path d="M7 12l4 4 6-7" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function HeartIcon({ size = 18, color = 'var(--text-primary)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function HeartFilledIcon({ size = 18, color = 'var(--danger)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill={color} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function StarIcon({ size = 16, color = 'var(--warning)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill={color} stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function SwitchIcon({ size = 16, color = 'var(--text-primary)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7 16V4m0 0L3 8m4-4l4 4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M17 8v12m0 0l4-4m-4 4l-4-4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function CheckIcon({ size = 16, color = 'var(--success)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5 12l5 5L20 7" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}


/* ───────────────────────────────────────────────────────────────────────────
   Lucide-style icons added for the palette redesign.
   2–2.4px stroke, 24px grid, currentColor-friendly. Pass a CSS variable as
   `color` (e.g. color="var(--text-accent)") so they follow the theme.
   ─────────────────────────────────────────────────────────────────────────── */

export function ChevronUpIcon({ size = 14, color = 'var(--text-accent)', strokeWidth = 2.4 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="m6 15 6-6 6 6" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ChevronDownIcon({ size = 14, color = 'var(--text-muted)', strokeWidth = 2.4 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="m6 9 6 6 6-6" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ChevronRightIcon({ size = 16, color = 'var(--text-muted)', strokeWidth = 2.2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="m9 6 6 6-6 6" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** rotate-ccw — undo the last dismissed card. */
export function RotateCcwIcon({ size = 19, color = 'var(--text-muted)', strokeWidth = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M3 12a9 9 0 1 0 2.6-6.4L3 8" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 3v5h5" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ArrowRightIcon({ size = 16, color = 'var(--text-link)', strokeWidth = 2.2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M4 12h15M13 6l6 6-6 6" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function PlusIcon({ size = 20, color = 'var(--tile-teal-fg)', strokeWidth = 2.2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 5v14M5 12h14" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </svg>
  );
}

export function ClockIcon({ size = 18, color = 'var(--tile-teal-fg)', strokeWidth = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth={strokeWidth} />
      <path d="M12 7.5V12l3 1.8" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function UsersIcon({ size = 18, color = 'var(--tile-royal-fg)', strokeWidth = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="9.5" cy="8" r="3.3" stroke={color} strokeWidth={strokeWidth} />
      <path d="M3.5 19.5c0-3 2.7-5 6-5s6 2 6 5" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
      <path d="M16.5 5.2a3.3 3.3 0 0 1 0 6.4M17.6 14.9c2 .7 3.4 2.4 3.4 4.6" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </svg>
  );
}

export function InfoIcon({ size = 18, color = 'var(--tile-teal-fg)', strokeWidth = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth={strokeWidth} />
      <path d="M12 11v5M12 8h.01" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </svg>
  );
}

export function MoreHorizontalIcon({ size = 20, color = 'var(--text-link)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="5.5" cy="12" r="1.7" fill={color} />
      <circle cx="12" cy="12" r="1.7" fill={color} />
      <circle cx="18.5" cy="12" r="1.7" fill={color} />
    </svg>
  );
}

/** sliders-horizontal — the Filters pill. */
export function SlidersIcon({ size = 13, color = 'var(--text-accent)', strokeWidth = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M3 7h11M18 7h3M3 17h5M12 17h9" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
      <circle cx="16" cy="7" r="2.1" stroke={color} strokeWidth={strokeWidth} />
      <circle cx="10" cy="17" r="2.1" stroke={color} strokeWidth={strokeWidth} />
    </svg>
  );
}

/** A larger X for the dismiss button in the swipe action row. */
export function XIcon({ size = 26, color = 'var(--btn-outline-text)', strokeWidth = 2.2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M6 6l12 12M18 6L6 18" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </svg>
  );
}

/** Solid heart — actions only (like, match). The outline HeartIcon is for nav. */
export function HeartSolidIcon({ size = 30, color = '#fff' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M12 20.6s-8.1-5.1-8.1-10.5a4.5 4.5 0 0 1 8.1-2.7 4.5 4.5 0 0 1 8.1 2.7c0 5.4-8.1 10.5-8.1 10.5z"
        fill={color}
      />
    </svg>
  );
}

/** message-square, at action-button weight. */
export function MessageSquareIcon({ size = 19, color = 'var(--text-muted)', strokeWidth = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M20 15.5a2.5 2.5 0 0 1-2.5 2.5H8l-4 3.5v-16A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SparkleIcon({ size = 18, color = 'var(--tile-teal-fg)', strokeWidth = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 3.5 13.8 9 19.5 10.8 13.8 12.6 12 18.2 10.2 12.6 4.5 10.8 10.2 9z" stroke={color} strokeWidth={strokeWidth} strokeLinejoin="round" />
    </svg>
  );
}

export function ShieldIcon({ size = 18, color = 'var(--tile-teal-fg)', strokeWidth = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 3.2 19 6v5.6c0 4.3-2.9 7.4-7 9.2-4.1-1.8-7-4.9-7-9.2V6z" stroke={color} strokeWidth={strokeWidth} strokeLinejoin="round" />
    </svg>
  );
}

/* Appearance toggle glyphs. Sun = "you are in light mode", moon = "you are in
   dark mode" — the button shows the CURRENT state, not the destination, which
   is the convention iOS users read fastest. */

export function SunIcon({ size = 18, color = 'currentColor', strokeWidth = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="4.2" stroke={color} strokeWidth={strokeWidth} />
      <path
        d="M12 2.6v2.2M12 19.2v2.2M21.4 12h-2.2M4.8 12H2.6M18.65 5.35l-1.55 1.55M6.9 17.1l-1.55 1.55M18.65 18.65 17.1 17.1M6.9 6.9 5.35 5.35"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function MoonIcon({ size = 18, color = 'currentColor', strokeWidth = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M20.5 14.3A8.6 8.6 0 0 1 9.7 3.5a8.6 8.6 0 1 0 10.8 10.8z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
    </svg>
  );
}
